// src/components/EmbedChat.js
import '../css/embed-chat.css';
import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';

function EmbedChat() {
  const urlParams = new URLSearchParams(window.location.search);
  const companyIdParam = urlParams.get("org");   // REQUIRED
  const widgetColor = urlParams.get("color") || "#4a3aff";

  // Apply dynamic widget color
  useEffect(() => {
    document.documentElement.style.setProperty("--widget-color", widgetColor);
  }, [widgetColor]);



  const [companyInfo, setCompanyInfo] = useState(null);
  const [companyId, setCompanyId] = useState(companyIdParam);
  const [question, setQuestion] = useState('');
  const [history, setHistory] = useState([]);
  const [sessionId] = useState(`embed_${Date.now()}`);
  const [isTyping, setIsTyping] = useState(false);
  const [isLoadingCompany, setIsLoadingCompany] = useState(true);

  const chatHistoryRef = useRef(null);

  // SCROLL TO BOTTOM
  const scrollToBottom = () => {
    if (chatHistoryRef.current) {
      chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    if (history.length > 0) scrollToBottom();
  }, [history]);

//   // READ WIDGET COLOR FROM SCRIPT TAG
//   useEffect(() => {
//     const scriptTag = document.currentScript || document.querySelector('script[src*="widget.js"]');
//     const widgetColor = scriptTag?.getAttribute('data-color') || '#4a3aff';
//     // Set CSS variable for dynamic theming
//     document.documentElement.style.setProperty('--widget-color', widgetColor);
//   }, []);

  // Greeting helper
  function getTimeOfDayGreeting(companyName) {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) return `Good morning! Welcome to ${companyName}. How can I assist you today?`;
    if (hour >= 12 && hour < 18) return `Good afternoon! Welcome to ${companyName}. How can I assist you today?`;
    if (hour >= 18 && hour < 22) return `Good evening! Welcome to ${companyName}. How can I assist you today?`;
    return `Hello! Welcome to ${companyName}. How can I assist you today?`;
  }

  // FETCH COMPANY DATA
  useEffect(() => {
    async function fetchCompany() {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/organization_by_id/${companyIdParam}`
        );

        setCompanyInfo(res.data);
        setCompanyId(res.data.id);

        const companyName = res.data.name || "Company";
        const companyNameUpper = companyName.toUpperCase();
        const rawGreeting = res.data.greeting_message?.trim() || getTimeOfDayGreeting(companyName);
        const greeting = rawGreeting.replace(companyName, companyNameUpper);

        setIsTyping(true);
        setTimeout(() => {
          setHistory([{ sender: "bot", text: greeting }]);
          setIsTyping(false);
        }, 1500);
      } catch (err) {
        console.error("Error loading company", err);
      } finally {
        setIsLoadingCompany(false);
      }
    }
    fetchCompany();
  }, [companyIdParam]);

  // HANDLE ASK
  const handleAsk = async () => {
    if (!question.trim() || !companyId || isTyping) return;

    const userMsg = { sender: "user", text: question };
    setHistory(prev => [...prev, userMsg]);
    setQuestion('');
    setIsTyping(true);

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/ask/`,
        { session_id: sessionId, question, company_id: companyId }
      );

      const botMsg = { sender: "bot", text: res.data.answer };
      setHistory(prev => [...prev, botMsg]);
    } catch (err) {
      console.error("Ask error", err);
      setHistory(prev => [...prev, { sender: "bot", text: "Sorry, something went wrong." }]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleAsk();
    }
  };

  if (isLoadingCompany || !companyInfo) {
    return (
      <div className="embed-loading">
        <div className="dots-loader"><span></span><span></span><span></span></div>
        <p>Loading chatbot…</p>
      </div>
    );
  }

  const { name, logo_url, whatsapp, phone } = companyInfo;

  return (
    <div className="embed-chat-container">
      {/* HEADER */}
      <div className="embed-chat-header">
        <h2 className="embed-title">{name}</h2>
        {(whatsapp || phone) && (
          <a
            className="embed-agent-btn"
            href={whatsapp ? `${whatsapp}` : `tel:${phone}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Talk to an agent →
          </a>
        )}
      </div>

      {/* CHAT BOX */}
      <div className="embed-chat-history" ref={chatHistoryRef}>
        {history.map((msg, i) => (
          <div
            key={i}
            className={msg.sender === "user" ? "embed-msg embed-user" : "embed-msg embed-bot"}
          >
            {msg.sender === "user" ? msg.text : <ReactMarkdown>{msg.text}</ReactMarkdown>}
          </div>
        ))}
        {isTyping && <div className="embed-typing">… typing</div>}
      </div>

      {/* INPUT */}
      <div className="embed-input-area">
        <textarea
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type your question…"
        />
        <button className="embed-send-btn" onClick={handleAsk}>Send</button>
      </div>

      {/* Footer */}
      <div className="embed-footer">
        Powered by <a href="https://www.querybox.app" target="_blank" rel="noopener noreferrer">QueryBox</a>
      </div>
    </div>
  );
}

export default EmbedChat;


// // src/components/EmbedChat.js
// import '../css/embed-chat.css';
// import React, { useState, useRef, useEffect } from 'react';
// import axios from 'axios';
// import ReactMarkdown from 'react-markdown';

// function EmbedChat() {
//   const urlParams = new URLSearchParams(window.location.search);
//   const companyIdParam = urlParams.get("org");   // REQUIRED
//   const [companyInfo, setCompanyInfo] = useState(null);

//   const [companyId, setCompanyId] = useState(companyIdParam);
//   const [question, setQuestion] = useState('');
//   const [history, setHistory] = useState([]);
//   const [sessionId] = useState(`embed_${Date.now()}`);
//   const [isTyping, setIsTyping] = useState(false);
//   const [isLoadingCompany, setIsLoadingCompany] = useState(true);

//   const chatHistoryRef = useRef(null);

//   // SCROLL TO BOTTOM
//   const scrollToBottom = () => {
//     if (chatHistoryRef.current) {
//       chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight;
//     }
//   };

//   useEffect(() => {
//     if (history.length > 0) scrollToBottom();
//   }, [history]);

//   // Greeting helper
//   function getTimeOfDayGreeting(companyName) {
//     const hour = new Date().getHours();
//     if (hour >= 5 && hour < 12) {
//       return `Good morning! Welcome to ${companyName}. How can I assist you today?`;
//     } else if (hour >= 12 && hour < 18) {
//       return `Good afternoon! Welcome to ${companyName}. How can I assist you today?`;
//     } else if (hour >= 18 && hour < 22) {
//       return `Good evening! Welcome to ${companyName}. How can I assist you today?`;
//     } else {
//       return `Hello! Welcome to ${companyName}. How can I assist you today?`;
//     }
//   }

  

//   // FETCH COMPANY DATA
//   useEffect(() => {
//     async function fetchCompany() {
//       try {
//         const res = await axios.get(
//           `${process.env.REACT_APP_API_BASE_URL}/organization_by_id/${companyIdParam}`
//         );

//         setCompanyInfo(res.data);
//         setCompanyId(res.data.id);

//         // Build greeting
//         const companyName = res.data.name || "Company";
//         const companyNameUpper = companyName.toUpperCase();

//         const rawGreeting = res.data.greeting_message?.trim() || getTimeOfDayGreeting(companyName);

//         const greeting = rawGreeting.replace(companyName, companyNameUpper);

//         // SHOW GREETING
//         setIsTyping(true);
//         setTimeout(() => {
//           setHistory([{ sender: "bot", text: greeting }]);
//           setIsTyping(false);
//         }, 1500);

//       } catch (err) {
//         console.error("Error loading company", err);
//       } finally {
//         setIsLoadingCompany(false);
//       }
//     }

//     fetchCompany();
//   }, [companyIdParam]);

//   // HANDLE ASK
//   const handleAsk = async () => {
//     if (!question.trim() || !companyId || isTyping) return;

//     const userMsg = { sender: "user", text: question };
//     setHistory(prev => [...prev, userMsg]);
//     setQuestion('');
//     setIsTyping(true);

//     try {
//       const res = await axios.post(
//         `${process.env.REACT_APP_API_BASE_URL}/ask/`,
//         {
//           session_id: sessionId,
//           question,
//           company_id: companyId,
//         }
//       );

//       const botMsg = { sender: "bot", text: res.data.answer };
//       setHistory(prev => [...prev, botMsg]);
//     } catch (err) {
//       console.error("Ask error", err);
//       setHistory(prev => [
//         ...prev,
//         { sender: "bot", text: "Sorry, something went wrong." }
//       ]);
//     } finally {
//       setIsTyping(false);
//     }
//   };

//   const handleKeyDown = (e) => {
//     if (e.key === "Enter" && !e.shiftKey) {
//       e.preventDefault();
//       handleAsk();
//     }
//   };

//   if (isLoadingCompany || !companyInfo) {
//     return (
//       <div className="embed-loading">
//         <div className="dots-loader"><span></span><span></span><span></span></div>
//         <p>Loading chatbot…</p>
//       </div>
//     );
//   }

//   const { name, logo_url, whatsapp, phone } = companyInfo;

  
//   return (
//     <div className="embed-chat-container">
//     {/* HEADER */}
//     <div className="embed-chat-header">
//         <h2 className="embed-title">{name}</h2>
//         {(whatsapp || phone) && (
//         <a
//             className="embed-agent-btn"
//             href={whatsapp ? `${whatsapp}` : `tel:${phone}`}
//             target="_blank"
//             rel="noopener noreferrer"
//         >
//             Talk to an agent →
//         </a>
//         )}
//     </div>

//     {/* CHAT BOX */}
//     <div className="embed-chat-history" ref={chatHistoryRef}>
//         {history.map((msg, i) => (
//         <div
//             key={i}
//             className={msg.sender === "user" ? "embed-msg embed-user" : "embed-msg embed-bot"}
//         >
//             {msg.sender === "user" ? msg.text : <ReactMarkdown>{msg.text}</ReactMarkdown>}
//         </div>
//         ))}
//         {isTyping && <div className="embed-typing">… typing</div>}
//     </div>

//     {/* INPUT */}
//     <div className="embed-input-area">
//         <textarea
//         value={question}
//         onChange={(e) => setQuestion(e.target.value)}
//         onKeyDown={handleKeyDown}
//         placeholder="Type your question…"
//         />
//         <button className="embed-send-btn" onClick={handleAsk}>Send</button>
//     </div>

//     {/* Footer */}
//     <div className="embed-footer">
//         Powered by <a href="https://www.querybox.app" target="_blank" rel="noopener noreferrer">QueryBox</a>
//     </div>
//     </div>

    
  
//   );
// }

// export default EmbedChat;
